import { z } from 'zod';
import type { EffectClass, JarvisPlan } from './jarvis-v1.js';

/**
 * Jarvis live TOOL declarations (B2 / IDEA-72 — ADR 0085 §2): the ONE source of truth
 * for the allowlisted `ui.command.*` toolset plus the `ui.plan.update` / `ui.ask`
 * structural tools, shared by BOTH sides of the wire so they can never drift:
 *
 * - the API gateway (`services/idea-engine-api/src/jarvis-tools.ts`) derives the tool
 *   declarations it OFFERS the model (name/description/JSON-schema) from this list;
 * - the web host (`services/idea-engine/src/jarvis/idea-engine-host.ts`) derives the
 *   client-side EXECUTORS (id/title/effect + the Zod arg parser) from the same list,
 *   with a compile-time check that each tool's parsed args are assignable to its
 *   CommandMap payload.
 *
 * SECURITY (non-negotiable, ADR 0085 §2): this list is a DECLARATION for the model's
 * benefit only. The CLIENT gate is the authority — the browser engine keys effects on
 * ITS registry, counts acts against the per-turn cap, and parks spend/destructive at
 * the operator's confirm gate for an engine-minted token. Nothing declared here (and
 * nothing a compromised gateway could declare) can bypass that gate; conversely a tool
 * absent from the HOST registry fails visibly as `not_found` no matter what the server
 * offered. Adding a row here is a deliberate act reviewed as an attack-surface change.
 *
 * Args are DELIBERATELY flat objects of bounded strings/enums: a tiny declarative
 * field DSL below derives both the Zod parser (client boundary) and the JSON schema
 * (model declaration) from one description — no zod→json-schema dependency, no drift.
 */

// ── The arg-field DSL ───────────────────────────────────────────────────────────

/**
 * One bounded argument field of a command tool / capability (flat by design — see
 * module doc). `string` and `number` fields may be `optional` (the payload key is
 * absent when omitted); `enum` is always required (the agent must pick a member).
 * B-CAP (IDEA-74) widened the DSL with `number` + `optional` so the catalog can
 * describe every command's payload, not just the hand-curated seven string/enum tools.
 */
export type JarvisCommandArgField =
  | {
      readonly kind: 'string';
      readonly name: string;
      readonly description: string;
      readonly minLength: number;
      /** Hard cap — tool args are model/broker-authored external input (size-capped at every seam). */
      readonly maxLength: number;
      /** When true the field is optional (omitted ⇒ the payload key is absent). */
      readonly optional?: boolean;
    }
  | {
      readonly kind: 'number';
      readonly name: string;
      readonly description: string;
      /** Inclusive bounds — a model/broker-authored number is clamped at the parse boundary. */
      readonly min?: number;
      readonly max?: number;
      readonly optional?: boolean;
    }
  | {
      readonly kind: 'enum';
      readonly name: string;
      readonly description: string;
      readonly values: readonly [string, ...string[]];
    };

/** One allowlisted command tool: metadata both wire sides derive from. */
export interface JarvisCommandToolMeta {
  /** The CommandMap key this tool dispatches (web-side compile-time checked). */
  readonly command: string;
  /** Operator-facing title (rendered at the confirm gate and in the transcript). */
  readonly title: string;
  /** Model-facing guidance (what the tool does, when to use it, what comes back). */
  readonly description: string;
  /** Effect class — DECLARATIVE here; the client registry re-declares and GATES on its own copy. */
  readonly effect: EffectClass;
  readonly fields: readonly JarvisCommandArgField[];
}

/** Stable tool id for a command tool (`ui.command.<command>`). */
export const jarvisCommandToolId = (meta: Pick<JarvisCommandToolMeta, 'command'>): string =>
  `ui.command.${meta.command}`;

/** The value TYPE one field produces: enum → its literal union, number → number, string → string. */
type JarvisFieldValue<F> = F extends { readonly values: readonly (infer V extends string)[] }
  ? V
  : F extends { readonly kind: 'number' }
    ? number
    : string;

/**
 * The parsed-args TYPE a meta/capability row's schema produces. Required fields become
 * required keys; `optional: true` string/number fields become optional keys — so the
 * derived shape is assignable to a `CommandMap` payload whose corresponding field is
 * optional (the compile-time lock idea-engine runs over the catalog).
 */
export type JarvisCommandToolArgs<M extends JarvisCommandToolMeta> = {
  readonly [F in Exclude<M['fields'][number], { readonly optional: true }> as F['name']]: JarvisFieldValue<F>;
} & {
  readonly [F in Extract<M['fields'][number], { readonly optional: true }> as F['name']]?: JarvisFieldValue<F>;
};

/** Build the Zod boundary parser for one meta/capability row (strict: unknown keys fail). */
export function jarvisCommandArgsSchema<M extends JarvisCommandToolMeta>(meta: M): z.ZodType<JarvisCommandToolArgs<M>> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of meta.fields) {
    let node: z.ZodTypeAny;
    if (field.kind === 'enum') {
      node = z.enum(field.values);
    } else if (field.kind === 'number') {
      let n = z.number();
      if (field.min !== undefined) n = n.min(field.min);
      if (field.max !== undefined) n = n.max(field.max);
      node = n;
      if (field.optional === true) node = node.optional();
    } else {
      node = z.string().min(field.minLength).max(field.maxLength);
      if (field.optional === true) node = node.optional();
    }
    shape[field.name] = node;
  }
  return z.object(shape).strict() as unknown as z.ZodType<JarvisCommandToolArgs<M>>;
}

/**
 * The JSON-schema node shape the tool declarations carry on the model wire. Structurally
 * a superset of the gateway's `JsonSchema` (extra `enum`/`maxLength` hints are legal JSON
 * Schema the provider honours) so the api can consume it without a cast chain.
 */
export interface JarvisToolJsonSchema {
  readonly type: 'object';
  readonly properties: Readonly<
    Record<
      string,
      {
        readonly type: 'string' | 'number';
        readonly description?: string;
        readonly enum?: readonly string[];
        readonly minLength?: number;
        readonly maxLength?: number;
        readonly minimum?: number;
        readonly maximum?: number;
      }
    >
  >;
  readonly required: readonly string[];
  readonly additionalProperties: false;
}

/** Build the model-facing JSON schema for one meta/capability row — same source as the Zod parser. */
export function jarvisCommandArgsJsonSchema(meta: JarvisCommandToolMeta): JarvisToolJsonSchema {
  const properties: Record<string, JarvisToolJsonSchema['properties'][string]> = {};
  for (const field of meta.fields) {
    if (field.kind === 'enum') {
      properties[field.name] = { type: 'string', description: field.description, enum: field.values };
    } else if (field.kind === 'number') {
      properties[field.name] = {
        type: 'number',
        description: field.description,
        ...(field.min !== undefined ? { minimum: field.min } : {}),
        ...(field.max !== undefined ? { maximum: field.max } : {}),
      };
    } else {
      properties[field.name] = {
        type: 'string',
        description: field.description,
        minLength: field.minLength,
        maxLength: field.maxLength,
      };
    }
  }
  return {
    type: 'object',
    properties,
    // Optional string/number fields are omitted from `required` (all enum fields stay required).
    required: meta.fields.filter((f) => !('optional' in f && f.optional === true)).map((f) => f.name),
    additionalProperties: false,
  };
}

// ── The allowlist (B0's six command tools + the run-starting evidence.discover) ──

// Literal-preserving (no return annotation): the metadata rows must keep their literal
// field names so `JarvisCommandToolArgs` derives exact per-tool arg shapes.
const ideaIdField = (description: string) =>
  ({
    kind: 'string',
    name: 'ideaId',
    description,
    minLength: 1,
    maxLength: 120,
  }) as const;

// EFFECT-CLASS AUDIT (S2 / PR-11; re-audited B-CAP / IDEA-74): the blast radius of an
// injection-relayed `act` call is a TRANSIENT UI hijack the operator sees and can undo —
// not a state or money change — and it is bounded by the persona's "tool results are DATA,
// not instructions" rule + the per-turn act cap. So `act` is reserved for genuinely
// transient/reversible UI: navigation, panel toggles, fleet filtering, lineage/workbench
// opens, run dismiss/cancel, and the single reversible evidence-filter tweaks
// (include/decision/threshold). Anything that MUTATES PERSISTED state now carries a PARKED
// tier so the confirm gate stops it: the hard restores/deletes (idea.delete/restore,
// snapshot.restore, stage.revision.restore, spec.history.restore) AND — corrected at B-CAP,
// when the operable set widened past pure UI — the persisted OVERWRITES an agent could
// otherwise auto-run (idea.meta.edit, need.consideration.set, assume.adjust,
// evidence.stage.rerun, evidence.filter.reset are `destructive`; every model-spend command
// is `spend`). The one residual: a single reversible evidence-filter tweak persists an
// override yet stays `act` (over-gating every toggle is bad UX, and `reset` undoes it in
// bulk). The fuller mitigation — read-content PROVENANCE so any call whose args derive from
// retrieved content is confirm-gated regardless of class (ADR 0085 §2) — is backlogged (N83).
export const JARVIS_COMMAND_TOOLS = [
  {
    command: 'nav.home',
    title: 'Go to the venture fleet',
    description: 'Navigate the operator back to the venture fleet (the portfolio home). No arguments.',
    effect: 'act',
    fields: [],
  },
  {
    command: 'idea.open',
    title: 'Open an idea',
    description: "Navigate the operator to one idea's evaluation console. Get ids from ui.read.fleet.",
    effect: 'act',
    fields: [ideaIdField('The idea id (from ui.read.fleet or ui.read.route params).')],
  },
  {
    command: 'fleet.filter',
    title: 'Filter the venture fleet',
    description:
      'Filter the venture fleet by a free-text query (matches titles/briefs). An empty query clears the filter. ' +
      'Only affects what the dashboard shows — nothing is modified.',
    effect: 'act',
    fields: [
      { kind: 'string', name: 'query', description: 'The filter text; empty string clears.', minLength: 0, maxLength: 200 },
    ],
  },
  {
    command: 'view.toggle',
    title: 'Toggle a console panel',
    description: 'Open/close one console panel by name.',
    effect: 'act',
    fields: [
      {
        kind: 'enum',
        name: 'panel',
        description: 'Which panel to toggle.',
        values: ['roster', 'legend', 'verdict', 'activity-tray'],
      },
    ],
  },
  {
    command: 'idea.delete',
    title: 'Delete an idea (soft-delete; undoable)',
    description:
      'Soft-delete one idea from the portfolio (recoverable via idea.restore / the Undo toast). ' +
      'DESTRUCTIVE: the operator must approve it at the confirm gate before anything runs.',
    effect: 'destructive',
    fields: [ideaIdField('The id of the idea to delete.')],
  },
  {
    // S2 (PR-11): restore is a STATE-MUTATING undo, so it carries the `destructive`
    // parked tier — the operator's confirm gate must stop it. `destructive` is the
    // honest "mutating, confirm it" bucket for a state-changing undo (the smaller
    // change vs. widening the parked-set membership). WHY it must park as a JARVIS
    // tool: prompt-injection riding verbatim read-content (e.g. an idea title telling
    // Jarvis to "restore idea-X") could otherwise auto-un-delete an idea the operator
    // deliberately removed. Only the JARVIS-tool classification changed; the app's own
    // Undo-toast `idea.restore` command dispatch is a DIFFERENT seam and is unaffected.
    command: 'idea.restore',
    title: 'Restore a deleted idea',
    description:
      'Restore a soft-deleted idea back into the portfolio. ' +
      'Confirm-gated: the operator must approve it at the confirm gate before anything runs.',
    effect: 'destructive',
    fields: [ideaIdField('The id of the deleted idea to restore.')],
  },
  {
    command: 'evidence.discover',
    title: 'Run evidence discovery (background run — paid model calls)',
    description:
      'Enqueue the evidence-discovery background run for one idea × stage × channel ' +
      '(search → fetch → extract → qualify). SPEND: it drives paid model calls; the operator must approve. ' +
      'Returns the started run id — completion lands in the activity feed (read it with ui.read.activity).',
    effect: 'spend',
    fields: [
      ideaIdField('The idea to discover evidence for.'),
      { kind: 'string', name: 'stage', description: "The stage KEY (e.g. 'tam' — from ui.read.idea stages).", minLength: 1, maxLength: 40 },
      { kind: 'string', name: 'channel', description: "The evidence channel for that stage (e.g. 'search').", minLength: 1, maxLength: 40 },
    ],
  },
] as const satisfies readonly JarvisCommandToolMeta[];

/** Union of the allowlisted command names (web-side compile-time lock-step keys on it). */
export type JarvisCommandToolName = (typeof JARVIS_COMMAND_TOOLS)[number]['command'];

const COMMAND_TOOL_IDS: ReadonlySet<string> = new Set(JARVIS_COMMAND_TOOLS.map((m) => jarvisCommandToolId(m)));

/** Whether `toolId` is one of the allowlisted `ui.command.*` tool ids. */
export const isJarvisCommandToolId = (toolId: string): boolean => COMMAND_TOOL_IDS.has(toolId);

// ── Structural tools: the plan and the ask (free — no app effect, gateway-mapped) ──

/** `ui.plan.update` — the model maintains its visible plan as DATA (ADR 0085 §3). */
export const JARVIS_PLAN_TOOL_ID = 'ui.plan.update';
/** `ui.ask` — a first-class clarifying question that pauses the turn for the operator. */
export const JARVIS_ASK_TOOL_ID = 'ui.ask';

export const JARVIS_PLAN_MAX_STEPS = 12;

/** Max suggested answers on a `ui.ask` — shared by the Zod parser and the model schema. */
export const JARVIS_ASK_MAX_OPTIONS = 6;

/**
 * Bounded plan revision — the `ui.plan.update` tool's args. Same shape as
 * {@link JarvisPlan} with hard caps on every string/array: the plan is model-authored
 * external input that is persisted and rendered, so an unbounded revision must fail
 * at the boundary, never reach storage or the panel.
 */
export const JarvisPlanUpdateArgsSchema = z
  .object({
    version: z.number().int().min(1).max(10_000),
    steps: z
      .array(
        z
          .object({
            id: z.string().min(1).max(64),
            title: z.string().min(1).max(200),
            kind: z.enum(['tool', 'ask', 'note']),
            status: z.enum(['pending', 'active', 'done', 'failed', 'skipped']),
            tool: z.string().min(1).max(120).optional(),
            dependsOn: z.array(z.string().min(1).max(64)).max(JARVIS_PLAN_MAX_STEPS).default([]),
            note: z.string().max(500).optional(),
          })
          .strict(),
      )
      .max(JARVIS_PLAN_MAX_STEPS),
  })
  .strict();
export type JarvisPlanUpdateArgs = z.infer<typeof JarvisPlanUpdateArgsSchema>;
// Compile-time: a parsed plan revision IS a JarvisPlan (the hop response / plan-update entry reuse it).
type _PlanArgsArePlans = JarvisPlanUpdateArgs extends JarvisPlan ? true : never;
const _planArgsArePlans: _PlanArgsArePlans = true;
void _planArgsArePlans;

/** Bounded ask — the `ui.ask` tool's args (maps to B0's `ask` entry type). */
export const JarvisAskArgsSchema = z
  .object({
    question: z.string().min(1).max(500),
    options: z.array(z.string().min(1).max(120)).min(1).max(JARVIS_ASK_MAX_OPTIONS).optional(),
  })
  .strict();
export type JarvisAskArgs = z.infer<typeof JarvisAskArgsSchema>;

/** Model-facing JSON schema for `ui.plan.update` (colocated with the Zod above — one module, one truth). */
export const JARVIS_PLAN_TOOL_JSON_SCHEMA = {
  type: 'object',
  properties: {
    version: { type: 'number', description: 'Monotonic plan version — bump it on every revision.' },
    steps: {
      type: 'array',
      description: `The full plan (max ${JARVIS_PLAN_MAX_STEPS} steps) — send the WHOLE plan each revision, not a diff.`,
      // Schema-level bounds (not prose-only): the plan is model-authored, persisted and
      // rendered, so the model gets the same caps its Zod boundary parser enforces.
      maxItems: JARVIS_PLAN_MAX_STEPS,
      items: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Stable step id (stable across revisions).', maxLength: 64 },
          title: { type: 'string', description: 'Short operator-readable step title.', maxLength: 200 },
          kind: { type: 'string', description: "'tool' | 'ask' | 'note'", enum: ['tool', 'ask', 'note'] },
          status: {
            type: 'string',
            description: "'pending' | 'active' | 'done' | 'failed' | 'skipped'",
            enum: ['pending', 'active', 'done', 'failed', 'skipped'],
          },
          tool: { type: 'string', description: "Tool id a 'tool' step executes (optional).", maxLength: 120 },
          dependsOn: {
            type: 'array',
            description: 'Step ids that must complete first.',
            maxItems: JARVIS_PLAN_MAX_STEPS,
            items: { type: 'string', maxLength: 64 },
          },
          note: { type: 'string', description: 'Optional short note.', maxLength: 500 },
        },
        required: ['id', 'title', 'kind', 'status'],
        additionalProperties: false,
      },
    },
  },
  required: ['version', 'steps'],
  additionalProperties: false,
} as const;

/** Model-facing JSON schema for `ui.ask`. */
export const JARVIS_ASK_TOOL_JSON_SCHEMA = {
  type: 'object',
  properties: {
    question: { type: 'string', description: 'The clarifying question to show the operator.', maxLength: 500 },
    options: {
      type: 'array',
      description: `Optional suggested answers (max ${JARVIS_ASK_MAX_OPTIONS}, each ≤120 chars).`,
      maxItems: JARVIS_ASK_MAX_OPTIONS,
      items: { type: 'string', maxLength: 120 },
    },
  },
  required: ['question'],
  additionalProperties: false,
} as const;

// ── Knowledge search tool (B5 / IDEA-80 — ADR 0090): GATEWAY-RESOLVED, owner-scoped ──
//
// `knowledge.search` is a SERVER-RESOLVED read tool (like `ui.capabilities.describe`), NOT a
// browser-relayed `ui.read.*`: the vector store + embedding key are server-side and the search
// MUST owner-scope with the request's DERIVED owner — the browser never supplies the owner. The
// gateway embeds the query, runs the owner-scoped nearest-neighbour search, and returns the top-K
// chunks as a synthetic tool-call/result pair. Offered (and resolvable) only when FEATURE_KNOWLEDGE
// is on. Best-effort: an empty index / embedding hiccup returns no matches, never fails the turn.

/** `knowledge.search` — search the operator's uploaded knowledge base (owner-scoped RAG). */
export const JARVIS_KNOWLEDGE_SEARCH_TOOL_ID = 'knowledge.search' as const;

/** Longest search query (bounds the single query-embed at the seam). */
export const KNOWLEDGE_QUERY_MAX_CHARS = 400;
/** Default number of chunks returned when `limit` is omitted. */
export const KNOWLEDGE_SEARCH_DEFAULT_LIMIT = 6;
/** Hard ceiling on chunks returned by one search (bounded result). */
export const KNOWLEDGE_SEARCH_MAX_LIMIT = 12;

/** Bounded `knowledge.search` args — a query, plus an optional top-K limit. */
export const JarvisKnowledgeSearchArgsSchema = z
  .object({
    query: z.string().min(1).max(KNOWLEDGE_QUERY_MAX_CHARS),
    limit: z.number().int().min(1).max(KNOWLEDGE_SEARCH_MAX_LIMIT).optional(),
  })
  .strict();
export type JarvisKnowledgeSearchArgs = z.infer<typeof JarvisKnowledgeSearchArgsSchema>;

/** Model-facing JSON schema for `knowledge.search` (same source as the Zod parser above). */
export const JARVIS_KNOWLEDGE_SEARCH_TOOL_JSON_SCHEMA = {
  type: 'object',
  properties: {
    query: {
      type: 'string',
      description: "What to look up in the operator's uploaded documents (a question or key phrase).",
      maxLength: KNOWLEDGE_QUERY_MAX_CHARS,
    },
    limit: {
      type: 'number',
      description: `How many passages to return (1–${KNOWLEDGE_SEARCH_MAX_LIMIT}; default ${KNOWLEDGE_SEARCH_DEFAULT_LIMIT}).`,
      minimum: 1,
      maximum: KNOWLEDGE_SEARCH_MAX_LIMIT,
    },
  },
  required: ['query'],
  additionalProperties: false,
} as const;

/**
 * Serialized-args ceiling for EVERY relayed tool call (red-team bound, N65): a model
 * (or hostile broker) fanning multi-kilobyte args into the engine/transcript/prompt
 * is refused at the seam that sees it first. Roomy for every legal toolset arg
 * (longest legal payload ≈ a 200-char query / a 12-step plan ≪ this).
 */
export const JARVIS_TOOL_ARGS_MAX_CHARS = 4_000;

// ════════════════════════════════════════════════════════════════════════════════
// B-CAP (IDEA-74) — the CAPABILITY CATALOG: drift-proof metadata for EVERY command
// ════════════════════════════════════════════════════════════════════════════════
//
// The single, versioned source of truth for what Jarvis can operate. Every registered
// CommandBus command (ADR 0032 — the semantic-action seam) has EXACTLY ONE catalog row:
// either agentOperable with full arg metadata, or non-operable with a `reason`. A new
// command with neither fails the idea-engine drift-proof completeness test (keyof
// CommandMap ⇄ catalog, both ways) — "every capability manipulable, none silently
// missing" by construction.
//
// SECURITY (ADR 0085 §2 — client-gate-is-authority, load-bearing): this catalog is a
// DECLARATION for the model/gateway's benefit. The browser engine re-derives effect +
// agentOperable from ITS OWN client registry (built from this same catalog) and gates
// on that. Nothing declared here — and nothing a compromised gateway could declare —
// bypasses the confirm gate; spend/destructive PARK, and the agent-loop meta-commands
// (jarvis.confirm/deny/interrupt/send) are agentOperable:false so the model can never
// approve its own gate. That denylist is the single most important line in this catalog.

/** Grouping for the compact system-prompt index (Pillar 2) + `ui.capabilities.describe`. */
export type JarvisCapabilityCategory =
  | 'navigation'
  | 'idea-lifecycle'
  | 'stage'
  | 'evidence'
  | 'need'
  | 'spec'
  | 'runs'
  | 'snapshots'
  | 'view'
  | 'jarvis';

/**
 * One capability = one command's agent metadata. Extends {@link JarvisCommandToolMeta}
 * (so every existing schema/json-schema/args helper works over a capability unchanged)
 * with the catalog fields: a category, whether the agent may operate it, and — when it
 * may NOT — an honest `reason` the index/describe never surfaces but the drift test reads.
 */
export interface JarvisCapability extends JarvisCommandToolMeta {
  readonly category: JarvisCapabilityCategory;
  /** True ⇒ a user-facing action the agent may invoke. False ⇒ off-limits (see `reason`). */
  readonly agentOperable: boolean;
  /** Required when `agentOperable` is false — WHY the agent may not operate this command. */
  readonly reason?: string;
}

// ── Field constructors (literal-preserving; the outer `as const` keeps names/enums exact) ──
const strF = <N extends string>(name: N, description: string, max = 200) =>
  ({ kind: 'string', name, description, minLength: 1, maxLength: max }) as const;
const optStrF = <N extends string>(name: N, description: string, max = 500) =>
  ({ kind: 'string', name, description, minLength: 0, maxLength: max, optional: true }) as const;
const numF = <N extends string>(name: N, description: string, min?: number, max?: number) =>
  ({
    kind: 'number',
    name,
    description,
    ...(min !== undefined ? { min } : {}),
    ...(max !== undefined ? { max } : {}),
  }) as const;
// `const V` preserves the literal enum tuple (so the derived args type is the exact
// member union, e.g. TogglePanel — not a widened `string`).
const enumF = <N extends string, const V extends readonly [string, ...string[]]>(name: N, description: string, values: V) =>
  ({ kind: 'enum', name, description, values }) as const;
const ideaIdF = (description: string) => strF('ideaId', description, 120);
// `stageKey` vs `stage`: most commands name the payload field `stageKey`; the two
// evidence-route commands (explorer.open, discover) name it `stage` — the field names
// MUST match the CommandMap payload keys (the idea-engine args lock enforces this).
const stageKeyF = strF('stageKey', "The stage KEY (e.g. 'tam' — from ui.read.idea stages).", 40);
const stageF = strF('stage', "The stage KEY (e.g. 'tam' — from ui.read.idea stages).", 40);

// ── Non-operable reasons ──────────────────────────────────────────────────────────
/** The agent-loop meta denylist reason — the load-bearing self-approval guard. */
const REASON_META =
  'Agent-loop meta-command: if the model could drive its own confirm/deny/interrupt/send it would ' +
  'self-approve its own spend/destructive gate and defeat the ADR 0085 safety model.';
const REASON_CHROME = "Jarvis's own UI chrome, not a portfolio capability.";
const REASON_PRIMITIVE = 'Low-level input-routing primitive; use the semantic nav/open commands instead.';
const REASON_LEAVES_ORIGIN =
  'Leaves the origin entirely: it would evict the operator from this service and end the live Jarvis session mid-turn. Returning to the OS front door is an operator decision, not an agent capability.';

/**
 * A `jarvis.*` chrome/meta capability row — always non-operable. The SHARED shape every
 * service's catalog includes: the `jarvis.*` command family is identical across consumers
 * (it's the dock's own input surface, `JarvisCommandMap`), so its non-operable rows —
 * INCLUDING the load-bearing self-approval denylist (confirm/deny/interrupt/send) — are
 * declared ONCE here and spread into each catalog (ADR 0096). Category is fixed 'jarvis'
 * (a member of every service catalog's category union).
 */
type JarvisChromeCapability = JarvisCommandToolMeta & {
  readonly category: 'jarvis';
  readonly agentOperable: false;
  readonly reason: string;
};

/**
 * The `jarvis.*` chrome/meta capabilities — SHARED across every service catalog (ADR 0096).
 * `as const` preserves the command literals so each catalog that spreads this keeps its
 * drift/args locks; every row is non-operable, so nothing here ever reaches `ui.command.invoke`.
 * The first four (confirm/deny/interrupt/send) are the self-approval denylist — the single
 * most important safety line in any Jarvis catalog (ADR 0085 §2).
 */
export const JARVIS_CHROME_CAPABILITIES = [
  // Agent-loop meta — MUST be false (self-approval hazard, the load-bearing security line).
  { command: 'jarvis.confirm', category: 'jarvis', agentOperable: false, reason: REASON_META, effect: 'act', title: 'Approve a pending call', description: "Approve Jarvis's parked spend/destructive call (operator-only).", fields: [] },
  { command: 'jarvis.deny', category: 'jarvis', agentOperable: false, reason: REASON_META, effect: 'act', title: 'Deny a pending call', description: "Deny Jarvis's parked call (operator-only).", fields: [] },
  { command: 'jarvis.interrupt', category: 'jarvis', agentOperable: false, reason: REASON_META, effect: 'act', title: 'Interrupt the turn', description: 'Abort the in-flight Jarvis turn (operator-only).', fields: [] },
  { command: 'jarvis.send', category: 'jarvis', agentOperable: false, reason: REASON_META, effect: 'act', title: 'Send a message', description: 'Start a Jarvis turn with an operator message (operator-only).', fields: [] },
  // Jarvis UI chrome — pointless for the agent, not portfolio capabilities.
  { command: 'jarvis.toggle', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Toggle the Jarvis dock', description: 'Open/close the Jarvis sidebar dock.', fields: [] },
  { command: 'jarvis.sheet', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Size the Jarvis sheet', description: 'Size the mobile Jarvis bottom sheet.', fields: [] },
  { command: 'jarvis.session.new', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'New Jarvis session', description: 'Start a fresh Jarvis session.', fields: [] },
  { command: 'jarvis.session.load', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Load a Jarvis session', description: 'Hydrate a past Jarvis session into the dock.', fields: [] },
  { command: 'jarvis.sessions.refresh', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Refresh Jarvis sessions', description: 'Reload the Jarvis sessions list.', fields: [] },
  { command: 'jarvis.sessions.toggle', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Toggle Jarvis sessions panel', description: 'Flip the Jarvis past-sessions panel.', fields: [] },
  // Jarvis session delete + Knowledge panel chrome (B5 / IDEA-80) — operator-only UI, not agent
  // capabilities (the agent's knowledge access is the gateway-resolved knowledge.search tool, which
  // is deliberately NOT a CommandBus command; the operator manages uploads + session deletion here).
  { command: 'jarvis.session.delete', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Delete a Jarvis session', description: 'Delete a past Jarvis session and its entries.', fields: [] },
  { command: 'jarvis.knowledge.toggle', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Toggle Jarvis knowledge panel', description: 'Flip the Jarvis uploaded-documents panel.', fields: [] },
  { command: 'jarvis.knowledge.refresh', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Refresh Jarvis knowledge', description: 'Reload the uploaded-documents list.', fields: [] },
  { command: 'jarvis.knowledge.upload', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Upload a knowledge document', description: 'Upload a text/markdown document to the operator knowledge base.', fields: [] },
  { command: 'jarvis.knowledge.delete', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Delete a knowledge document', description: 'Delete one uploaded knowledge document and its chunks.', fields: [] },
  // Voice dictation chrome (B7 / IDEA-82 — ADR 0092) — operator-only INPUT affordance: the mic
  // populates the composer with browser speech-to-text; the operator still reviews + sends. Not an
  // agent capability (an agent already dispatches commands directly — it needs no microphone).
  { command: 'jarvis.dictation.toggle', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Toggle voice dictation', description: 'Start/stop browser voice dictation into the Jarvis composer.', fields: [] },
  { command: 'jarvis.dictation.insert', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Insert dictated text', description: 'Insert a recognized speech transcript into the Jarvis composer input.', fields: [] },
  // Expand-to-overlay chrome (IDEA-83 inc 4 / ADR 0100 pillar 5) — a FIXED, trusted control (like the
  // sheet-size toggle) that pops one transcript widget into a focus-trapped overlay. Non-operable: an
  // agent already has the widget's data and dispatches commands directly — it needs no pop-out.
  { command: 'jarvis.widget.expand', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Expand a transcript widget', description: 'Expand one transcript widget into a focus-trapped pop-out overlay.', fields: [] },
  { command: 'jarvis.widget.collapse', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Collapse the widget overlay', description: 'Close the expanded Jarvis widget overlay.', fields: [] },
  // Interactive-widget action conduit (IDEA-83 inc 3 / ADR 0100 pillar 4) — the OPERATOR-activated
  // dispatch seam for a model-authored widget's action. MUST be agentOperable:false: the model can
  // never call it itself (that would let it self-dispatch an arbitrary command, laundering an effect
  // past the gate); it can only EMIT a widget whose action the operator activates, which the service
  // handler then resolves→parses→classifies→(park|dispatch|refuse) through the CLIENT registry gate.
  { command: 'jarvis.widget.action', category: 'jarvis', agentOperable: false, reason: REASON_CHROME, effect: 'act', title: 'Activate a widget action', description: 'Activate one interactive Jarvis widget action (operator-only; gated through the client capability registry).', fields: [] },
] as const satisfies readonly JarvisChromeCapability[];

/**
 * THE IDEA ENGINE'S CAPABILITY CATALOG — one service's rows, not a generic default.
 * It lives in shared `contracts` (rather than in `services/idea-engine`) only because the
 * Idea Engine's gateway (`idea-engine-api`) is a separate service that can reach it no other
 * way; the web app + the gateway both consume it from here. Each consumer keeps its OWN
 * catalog the same way — see `command-center-jarvis-v1.ts` for the sibling pattern — and every
 * catalog spreads in the shared {@link JARVIS_CHROME_CAPABILITIES}. `as const satisfies` keeps
 * every `command`/`agentOperable`/`fields` literal (so idea-engine can derive per-command arg
 * types + assignability) while the `satisfies` clause type-checks each row against
 * {@link JarvisCapability}.
 */
export const JARVIS_CAPABILITIES = [
  // ── navigation (semantic; act — a transient UI hijack at worst, operator-undoable) ──
  { command: 'nav.home', category: 'navigation', agentOperable: true, effect: 'act', title: 'Go to the venture fleet', description: 'Return the operator to the venture fleet (the portfolio home).', fields: [] },
  { command: 'nav.back', category: 'navigation', agentOperable: true, effect: 'act', title: 'Go back a level', description: 'Undock / go back one navigation level.', fields: [] },
  // Return to the OS front door (COMMAND-4 / ADR 0110). Non-operable: unlike every other
  // nav command this LEAVES THE ORIGIN — it would evict the operator from the Idea Engine
  // and end the live Jarvis session mid-turn. Going home is the operator's call, never the
  // agent's; the model has no reason to want it and no way to undo it.
  { command: 'nav.commandCenter', category: 'navigation', agentOperable: false, reason: REASON_LEAVES_ORIGIN, effect: 'act', title: 'Return to the Command Center', description: 'Leave the Idea Engine for the SquadLab OS front door (the Command Center).', fields: [] },
  { command: 'nav.station', category: 'navigation', agentOperable: true, effect: 'act', title: 'Jump to a stage station', description: 'Jump directly to one evaluation stage station by its stage key.', fields: [stageKeyF] },
  { command: 'idea.open', category: 'navigation', agentOperable: true, effect: 'act', title: 'Open an idea', description: "Open one idea's evaluation console. Get ids from ui.read.fleet.", fields: [ideaIdF('The idea id (from ui.read.fleet or ui.read.route params).')] },
  { command: 'spec.open', category: 'navigation', agentOperable: true, effect: 'act', title: 'Open the build spec', description: "Navigate to an idea's build specification page (view only; it never generates on mount — use spec.regenerate to (re)author it).", fields: [ideaIdF('The idea whose build spec to open.')] },
  { command: 'evidence.explorer.open', category: 'navigation', agentOperable: true, effect: 'act', title: 'Open the evidence explorer', description: 'Open the stagewise evidence explorer for one idea × stage × channel.', fields: [ideaIdF('The idea id.'), stageF, strF('channel', "The evidence channel (e.g. 'search').", 40)] },
  { command: 'evidence.workbench.open', category: 'navigation', agentOperable: true, effect: 'act', title: 'Open the evidence workbench', description: "Open one stage's evidence filter workbench (enters edit mode).", fields: [stageKeyF] },
  { command: 'need.open', category: 'navigation', agentOperable: true, effect: 'act', title: 'Open the real-need assessment', description: "Open an idea's structured real-need assessment surface.", fields: [ideaIdF('The idea id.')] },
  { command: 'trace.open', category: 'navigation', agentOperable: true, effect: 'act', title: 'Open a datum lineage', description: "Open/close one datum's provenance lineage (vendor→DB→gateway→screen).", fields: [stageKeyF, strF('field', 'The datum/field key within the stage.', 80)] },

  // ── snapshots ──
  { command: 'snapshot.open', category: 'snapshots', agentOperable: true, effect: 'act', title: 'Open a history snapshot', description: "Open one history snapshot's page for an idea.", fields: [ideaIdF('The idea id.'), strF('versionId', 'The snapshot version id.', 120)] },
  { command: 'snapshot.restore', category: 'snapshots', agentOperable: true, effect: 'destructive', title: 'Restore a history snapshot', description: 'Restore a history snapshot onto the live idea (OVERWRITES current state). Confirm-gated.', fields: [ideaIdF('The idea id.'), strF('versionId', 'The snapshot version id to restore.', 120)] },

  // ── idea-lifecycle ──
  { command: 'idea.generate', category: 'idea-lifecycle', agentOperable: true, effect: 'spend', title: 'Generate a new idea', description: 'Autonomously generate + score a new venture idea (paid model calls). Optionally steer it with guidelines.', fields: [optStrF('guidelines', 'Optional steer: a segment, audience, market or constraint.', 400)] },
  { command: 'idea.delete', category: 'idea-lifecycle', agentOperable: true, effect: 'destructive', title: 'Delete an idea (soft-delete; undoable)', description: 'Soft-delete one idea from the portfolio (recoverable via idea.restore / Undo). Confirm-gated.', fields: [ideaIdF('The id of the idea to delete.')] },
  { command: 'idea.restore', category: 'idea-lifecycle', agentOperable: true, effect: 'destructive', title: 'Restore a deleted idea', description: 'Restore a soft-deleted idea back into the portfolio. Confirm-gated.', fields: [ideaIdF('The id of the deleted idea to restore.')] },
  { command: 'idea.meta.edit', category: 'idea-lifecycle', agentOperable: true, effect: 'destructive', title: 'Edit an idea title/brief', description: "Overwrite the active idea's operator-authored title and brief (a history row is bracketed). Confirm-gated: the operator approves it before anything is written — a persisted overwrite of operator content is never silent.", fields: [strF('title', 'The new title (non-empty).', 200), strF('brief', 'The new description/brief.', 2000)] },
  { command: 'engine.evaluate', category: 'idea-lifecycle', agentOperable: true, effect: 'spend', title: 'Re-run the full evaluation', description: 'Re-run the whole idea evaluation (paid model calls). Optionally steer the run.', fields: [optStrF('steer', 'Optional run-wide operator steer.', 400)] },

  // ── stage ──
  { command: 'stage.refine', category: 'stage', agentOperable: true, effect: 'spend', title: 'Refine one evaluation stage', description: 'Regenerate one evaluation stage from operator feedback (paid model calls).', fields: [stageKeyF, strF('feedback', 'What to change about the stage.', 1000)] },
  { command: 'stage.revision.restore', category: 'stage', agentOperable: true, effect: 'destructive', title: 'Restore a stage revision', description: "Restore one stage's earlier refinement revision (re-applies its assumptions; overwrites). Confirm-gated.", fields: [stageKeyF, strF('revisionId', 'The revision id to restore.', 120)] },
  { command: 'assume.adjust', category: 'stage', agentOperable: true, effect: 'destructive', title: 'Adjust an assumption', description: 'Overwrite one stage assumption field to a number and recompute live (persisted, history-bracketed). Confirm-gated: the operator approves the change before it is written.', fields: [stageKeyF, strF('field', 'The assumption field key.', 80), numF('value', 'The new numeric value.')] },
  { command: 'sim.adjust', category: 'stage', agentOperable: true, effect: 'act', title: 'Tune a simulation input', description: 'Tune one bottom-up simulation input by dotted model path and re-simulate.', fields: [strF('path', "Dotted model path (e.g. 'monthlyChurnRate').", 120), numF('value', 'The new numeric value.')] },
  { command: 'idea.stages.fill', category: 'stage', agentOperable: true, effect: 'spend', title: 'Fill missing stages', description: 'Generate the evaluation stages not yet generated (paid model calls).', fields: [] },
  { command: 'insights.backfill', category: 'stage', agentOperable: true, effect: 'spend', title: 'Backfill sub-metric insights', description: 'Author the missing per-sub-metric insights for an already-evaluated idea (streaming model calls).', fields: [] },
  { command: 'insights.backfill.cancel', category: 'stage', agentOperable: true, effect: 'act', title: 'Cancel the insights backfill', description: 'Stop an in-flight insights backfill; already-saved stages are kept.', fields: [] },

  // ── view ──
  { command: 'view.toggle', category: 'view', agentOperable: true, effect: 'act', title: 'Toggle a console panel', description: 'Open/close one console panel by name.', fields: [enumF('panel', 'Which panel to toggle.', ['roster', 'legend', 'verdict', 'activity-tray'])] },
  { command: 'fleet.filter', category: 'view', agentOperable: true, effect: 'act', title: 'Filter the venture fleet', description: 'Filter the venture fleet by free-text query (empty clears). Only changes what the dashboard shows.', fields: [{ kind: 'string', name: 'query', description: 'The filter text; empty string clears.', minLength: 0, maxLength: 200 }] },

  // ── evidence ──
  { command: 'evidence.discover', category: 'evidence', agentOperable: true, effect: 'spend', title: 'Run evidence discovery (background run)', description: 'Enqueue the evidence-discovery background run for one idea × stage × channel (paid model calls). Returns the started run id; watch ui.read.activity.', fields: [ideaIdF('The idea to discover evidence for.'), stageF, strF('channel', "The evidence channel (e.g. 'search').", 40)] },
  { command: 'evidence.gate.toggle', category: 'evidence', agentOperable: true, effect: 'act', title: 'Toggle a source lineage', description: "Open/close one source's lineage in a stage's evidence gate.", fields: [stageKeyF, strF('sourceId', 'The source id.', 120)] },
  { command: 'evidence.filter.editToggle', category: 'evidence', agentOperable: true, effect: 'act', title: 'Toggle the evidence workbench', description: "Toggle a stage's evidence gate between read-only and editable.", fields: [stageKeyF] },
  { command: 'evidence.filter.include', category: 'evidence', agentOperable: true, effect: 'act', title: 'Include/exclude a source', description: "Keep a source in / hard-exclude it from a stage's evidence filter.", fields: [stageKeyF, strF('sourceId', 'The source id.', 120)] },
  { command: 'evidence.filter.decision', category: 'evidence', agentOperable: true, effect: 'act', title: 'Force a source decision', description: "Force a source's evidence decision (or 'auto').", fields: [stageKeyF, strF('sourceId', 'The source id.', 120), enumF('decision', 'The forced decision.', ['use', 'use-cautiously', 'proxy-only', 'reject', 'auto'])] },
  { command: 'evidence.filter.threshold', category: 'evidence', agentOperable: true, effect: 'act', title: 'Nudge a filter threshold', description: "Nudge a stage's evidence filter threshold in [0,1].", fields: [stageKeyF, enumF('which', 'Which threshold.', ['rejectFloor', 'highBand']), numF('value', 'The new threshold in [0,1].', 0, 1)] },
  { command: 'evidence.filter.reset', category: 'evidence', agentOperable: true, effect: 'destructive', title: 'Reset evidence filter overrides', description: "Clear ALL operator overrides for a stage's evidence filter (restore automatic). Confirm-gated: wiping the operator's saved overrides in bulk is never silent.", fields: [stageKeyF] },
  { command: 'evidence.belief.set', category: 'evidence', agentOperable: true, effect: 'act', title: 'Set belief bounds', description: 'Set operator belief bounds (low–high) on one conversion assumption; re-triangulates live.', fields: [stageKeyF, strF('channel', 'The evidence channel.', 40), strF('key', 'The conversion assumption key.', 80), numF('low', 'The low bound.'), numF('high', 'The high bound.')] },
  { command: 'evidence.stage.rerun', category: 'evidence', agentOperable: true, effect: 'destructive', title: 'Re-run a stage from evidence', description: "Re-run a stage from its filtered evidence (feeds the evidence estimate into the stage's assumptions — a persisted overwrite). Confirm-gated before it writes.", fields: [stageKeyF] },

  // ── need ──
  { command: 'need.consideration.set', category: 'need', agentOperable: true, effect: 'destructive', title: 'Set a real-need verdict', description: "Overwrite one real-need consideration's verdict on an idea (persisted operator judgment). Confirm-gated: the operator approves before their recorded verdict is changed.", fields: [ideaIdF('The idea id.'), strF('key', 'The consideration key.', 80), enumF('verdict', 'The verdict.', ['yes', 'maybe', 'no'])] },
  { command: 'need.analyze', category: 'need', agentOperable: true, effect: 'spend', title: 'Analyse real need with AI', description: "Run the background 'Analyse with AI' pass over an idea's real-need considerations (paid model calls).", fields: [ideaIdF('The idea id.')] },

  // ── spec ──
  { command: 'spec.download', category: 'spec', agentOperable: true, effect: 'act', title: 'Download the build prompt', description: 'Download the current build spec as a single-shot build-prompt markdown file.', fields: [] },
  { command: 'spec.regenerate', category: 'spec', agentOperable: true, effect: 'spend', title: 'Generate/refine the build spec', description: 'Generate (blank feedback) or refine (non-blank) the build spec, saving a new version (paid model calls).', fields: [optStrF('feedback', 'Optional refinement feedback; blank ⇒ fresh generation.', 1000)] },
  { command: 'spec.history.restore', category: 'spec', agentOperable: true, effect: 'destructive', title: 'Restore a spec version', description: 'Restore a historical build-spec version (overwrites the current spec). Confirm-gated.', fields: [strF('versionId', 'The spec version id to restore.', 120)] },

  // ── runs ──
  { command: 'run.cancel', category: 'runs', agentOperable: true, effect: 'act', title: 'Cancel a background run', description: 'Cancel one in-flight background run (non-terminal tasks move to cancelled).', fields: [strF('runId', 'The run id.', 120)] },
  { command: 'run.task.retry', category: 'runs', agentOperable: true, effect: 'spend', title: 'Retry a failed run task', description: 'Re-queue one failed background-run task (re-runs paid work).', fields: [strF('runId', 'The run id.', 120), strF('taskId', 'The task id.', 120)] },
  { command: 'run.dismiss', category: 'runs', agentOperable: true, effect: 'act', title: 'Dismiss a run entry', description: 'Drop one terminal/failed background-run entry from the activity surfaces.', fields: [strF('key', 'The activity entry key.', 120)] },

  // ══ NON-OPERABLE ═══════════════════════════════════════════════════════════════
  // The shared jarvis.* chrome + the self-approval denylist (ADR 0096 — declared once,
  // spread into every service catalog). Category 'jarvis'; all non-operable.
  ...JARVIS_CHROME_CAPABILITIES,
  // Raw input-routing primitives — device-agnostic nav plumbing, not semantic actions.
  { command: 'nav.move', category: 'navigation', agentOperable: false, reason: REASON_PRIMITIVE, effect: 'act', title: 'Move spatial focus', description: 'Move spatial focus (FocusLayer resolves the nearest node).', fields: [] },
  { command: 'nav.activate', category: 'navigation', agentOperable: false, reason: REASON_PRIMITIVE, effect: 'act', title: 'Activate the focused node', description: 'Activate the currently focused node.', fields: [] },
  { command: 'nav.step', category: 'navigation', agentOperable: false, reason: REASON_PRIMITIVE, effect: 'act', title: 'Step along the path', description: 'Fly to the next/previous station along the path.', fields: [] },
  { command: 'nav.adjust', category: 'navigation', agentOperable: false, reason: REASON_PRIMITIVE, effect: 'act', title: 'Nudge the focused control', description: 'Nudge the currently focused control (gamepad trigger / voice).', fields: [] },
  // Complex-payload — not expressible in the flat agent arg DSL (operator-only for now; backlog N81).
  { command: 'evidence.apply', category: 'evidence', agentOperable: false, reason: 'Payload is a nested evidence patch/range record not expressible in the flat agent arg DSL; the operator applies it from the evidence explorer. Backlog: richer arg DSL.', effect: 'act', title: 'Apply an evidence estimate', description: 'Atomically apply an evidence estimate (patch + ranges) to a stage.', fields: [] },
  { command: 'idea.element.generate', category: 'idea-lifecycle', agentOperable: false, reason: 'Payload is a discriminated-union GenTarget not expressible in the flat agent arg DSL; a dev stopgap surface. Backlog: richer arg DSL.', effect: 'spend', title: 'Generate one element', description: 'Generate/regenerate one backend-derived element (stage/explanation/evidence/source/spec).', fields: [] },
] as const satisfies readonly JarvisCapability[];

/** Union of every catalogued command name (idea-engine locks this against `keyof CommandMap`). */
export type JarvisCapabilityCommand = (typeof JARVIS_CAPABILITIES)[number]['command'];

/** The agent-OPERABLE subset — the only commands the index lists and `ui.command.invoke` runs. */
export const JARVIS_OPERABLE_CAPABILITIES = JARVIS_CAPABILITIES.filter(
  (c) => c.agentOperable,
) as readonly JarvisCapability[];

const CAPABILITY_BY_COMMAND: ReadonlyMap<string, JarvisCapability> = new Map(
  JARVIS_CAPABILITIES.map((c) => [c.command, c]),
);

/** Look up one capability by command name (undefined ⇒ not a catalogued command). */
export const jarvisCapability = (command: string): JarvisCapability | undefined => CAPABILITY_BY_COMMAND.get(command);

// ── The generic executor + discovery tools (Pillar 2 + 3) ──────────────────────────

/** `ui.command.invoke` — the ONE generic executor tool (subsumes the per-command tools). */
export const JARVIS_CAPABILITY_INVOKE_TOOL_ID = 'ui.command.invoke' as const;
/** `ui.capabilities.describe` — the FREE deterministic catalog-lookup discovery tool. */
export const JARVIS_CAPABILITY_DESCRIBE_TOOL_ID = 'ui.capabilities.describe' as const;

/** Max capability descriptors one `ui.capabilities.describe` call returns (bounded result). */
export const JARVIS_DESCRIBE_MAX_RESULTS = 20;

/** `ui.command.invoke` args — a command id + its (command-specific) args object. */
export const JarvisInvokeArgsSchema = z
  .object({
    command: z.string().min(1).max(120),
    args: z.record(z.unknown()).default({}),
  })
  .strict();
export type JarvisInvokeArgs = z.infer<typeof JarvisInvokeArgsSchema>;

/** `ui.capabilities.describe` args — narrow by command, category, or free-text query (all optional). */
export const JarvisDescribeArgsSchema = z
  .object({
    command: z.string().min(1).max(120).optional(),
    category: z.string().min(1).max(60).optional(),
    query: z.string().min(1).max(200).optional(),
  })
  .strict();
export type JarvisDescribeArgs = z.infer<typeof JarvisDescribeArgsSchema>;

/** One capability's full arg schema, as `ui.capabilities.describe` returns it. */
export interface JarvisCapabilityDescriptor {
  readonly command: string;
  readonly title: string;
  readonly description: string;
  readonly effect: EffectClass;
  readonly category: JarvisCapabilityCategory;
  readonly args: JarvisToolJsonSchema;
}

/**
 * The shape BOTH catalogs share — the Idea Engine's `JarvisCapability` and the Command
 * Center's `CommandCenterCapability` (COMMAND-2 / J3). Naming it lets the two projections
 * below serve either catalog, so a second gateway reuses discovery + the prompt index
 * instead of re-implementing them against its own rows.
 */
export interface JarvisCatalogRow extends JarvisCommandToolMeta {
  readonly category: string;
  readonly agentOperable: boolean;
}

const describeOne = (c: JarvisCatalogRow): JarvisCapabilityDescriptor => ({
  command: c.command,
  title: c.title,
  description: c.description,
  effect: c.effect,
  category: c.category as JarvisCapabilityCategory,
  args: jarvisCommandArgsJsonSchema(c),
});

/**
 * Resolve a `ui.capabilities.describe` request DETERMINISTICALLY over ANY catalog — no
 * model callout, no side effects (the "discovery is free" property, Pillar 2). Only
 * OPERABLE capabilities are describable (the model can only invoke those). Matching:
 * exact `command`; else `category`; else `query` substring over command/title/description;
 * else all operable. Results are capped at {@link JARVIS_DESCRIBE_MAX_RESULTS}.
 */
export function describeCatalogCapabilities(
  catalog: readonly JarvisCatalogRow[],
  request: JarvisDescribeArgs,
): { readonly capabilities: readonly JarvisCapabilityDescriptor[]; readonly total: number } {
  let matched: readonly JarvisCatalogRow[];
  if (request.command !== undefined) {
    const c = catalog.find((row) => row.command === request.command);
    matched = c && c.agentOperable ? [c] : [];
  } else if (request.category !== undefined) {
    matched = catalog.filter((c) => c.agentOperable && c.category === request.category);
  } else if (request.query !== undefined) {
    const q = request.query.toLowerCase();
    matched = catalog.filter(
      (c) =>
        c.agentOperable &&
        (c.command.toLowerCase().includes(q) || c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)),
    );
  } else {
    matched = catalog.filter((c) => c.agentOperable);
  }
  return { capabilities: matched.slice(0, JARVIS_DESCRIBE_MAX_RESULTS).map(describeOne), total: matched.length };
}

/** The Idea Engine's discovery resolver — {@link describeCatalogCapabilities} over its catalog. */
export function describeCapabilities(request: JarvisDescribeArgs): {
  readonly capabilities: readonly JarvisCapabilityDescriptor[];
  readonly total: number;
} {
  return describeCatalogCapabilities(JARVIS_CAPABILITIES, request);
}

/**
 * The compact ALWAYS-PRESENT capability index for the system prompt (Pillar 2 — a few
 * hundred tokens): the operable capabilities grouped by category, one line each
 * (`command — description — effect`). Generated from the catalog so it can never drift.
 * This gives Jarvis awareness of the whole board to PLAN from an instruction without a
 * discovery round-trip; full arg schemas are pulled on demand via ui.capabilities.describe.
 */
export function buildCatalogCapabilityIndex(catalog: readonly JarvisCatalogRow[]): string {
  const byCategory = new Map<string, JarvisCatalogRow[]>();
  for (const c of catalog) {
    if (!c.agentOperable) continue;
    const list = byCategory.get(c.category) ?? [];
    list.push(c);
    byCategory.set(c.category, list);
  }
  const lines: string[] = [];
  for (const [category, caps] of byCategory) {
    lines.push(category.toUpperCase());
    for (const c of caps) lines.push(`- ${c.command} — ${c.description} — ${c.effect}`);
  }
  return lines.join('\n');
}

/** The Idea Engine's prompt index — {@link buildCatalogCapabilityIndex} over its catalog. */
export function buildCapabilityIndex(): string {
  return buildCatalogCapabilityIndex(JARVIS_CAPABILITIES);
}

/** Model-facing JSON schema for `ui.command.invoke` (args stay open — the command's own schema gates them client-side). */
export const JARVIS_INVOKE_TOOL_JSON_SCHEMA = {
  type: 'object',
  properties: {
    command: {
      type: 'string',
      description: 'The capability command id to invoke (from the capability index or ui.capabilities.describe), e.g. "idea.delete".',
    },
    args: {
      type: 'object',
      description: "The command's arguments as a flat object. Pull the exact schema with ui.capabilities.describe first. Use {} for no-arg commands.",
      additionalProperties: true,
    },
  },
  required: ['command'],
  additionalProperties: false,
} as const;

/** Model-facing JSON schema for `ui.capabilities.describe`. */
export const JARVIS_DESCRIBE_TOOL_JSON_SCHEMA = {
  type: 'object',
  properties: {
    command: { type: 'string', description: 'Describe exactly this capability command id.' },
    category: { type: 'string', description: 'Describe all operable capabilities in this category.' },
    query: { type: 'string', description: 'Describe operable capabilities matching this free-text query.' },
  },
  required: [],
  additionalProperties: false,
} as const;
